import React, { ChangeEvent } from 'react'
import { CardElement } from '@stripe/react-stripe-js'
import { Else, If, Then, When } from 'react-if'
import { Button } from '@/atoms/Button'
import { Input } from '@/atoms/Input'
import { PaymentInfo } from '@/services/PifCheckoutService'
import { Offer } from '@/services/PifService'
import { isSupportedStripeCurrency } from '@/services/PifService/PifService'
import { formatPrice } from '@/services/PriceFormatter'
import { useUser } from '@/services/UserService'
import { isValidEmail, isValidName } from '@/utils/StripeFormValidator'
import { Translate, useTranslate } from '@/utils/translate/translate-client'
import { PrefilledCreditCardInput } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/PrefilledCreditCardInput'
import { useAppleGooglePay } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/useAppleGooglePay'
import { usePayItForwardForm } from '@/views/PayItForward/PayItForwardProjectCheckoutOfferView/PayItForwardStripeForm/usePayItForwardForm'
import { ExpressCheckoutMethods } from './ExpressCheckoutMethods'

export interface StripeFormNextProps {
  offer: Offer
  projectSlug?: string
  clientSecret: string
}

export const PayItForwardStripeForm: React.FC<StripeFormNextProps> = ({ offer, projectSlug, clientSecret }) => {
  const { user } = useUser()

  const {
    name,
    setName,
    email,
    setEmail,
    errorMessage,
    isSubmitting,
    savedPaymentInfo,
    setSavedPaymentInfo,
    checkout,
    onPayItForwardComplete,
  } = usePayItForwardForm()

  const {
    isSubmittingWithWallet,
    errorMessageWithWallet,
    paymentRequest: walletPaymentRequest,
  } = useAppleGooglePay({
    clientSecret,
    onComplete: onPayItForwardComplete,
    offer,
    isGuest: !user,
  })

  const { t } = useTranslate('pif')

  const nameFieldLabel = t('nameOnCard', 'Name on card')
  const emailFieldLabel = t('emailAddress', 'Email')
  const amount = formatPrice(offer.price, { currency: offer.currency })
  const amountToPayOneTime = t('amountToPayOneTime', 'Pay {{amount}}', {
    amount,
  })

  return (
    <form onSubmit={checkout}>
      <>
        <ExpressCheckoutMethods
          disableOrSection={true}
          walletPaymentRequest={walletPaymentRequest}
          projectSlug={projectSlug as string}
          onPayItForwardComplete={onPayItForwardComplete}
          name={name}
          offer={offer}
          enablePaypal={true}
        />
        {walletPaymentRequest && (
          <div className="flex flex-row items-center">
            <div className="h-0.5 grow-1 bg-core-gray-300" />
            <div className="p-3 uppercase text-core-gray-600">
              <Translate t={t} i18nKey="or">
                or
              </Translate>
            </div>
            <div className="h-0.5 grow-1 bg-core-gray-300" />
          </div>
        )}
      </>
      <When condition={isSupportedStripeCurrency(offer.currency)}>
        <div>
          <Input
            required
            usePlaceholderAsLabel
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            label={nameFieldLabel}
            isValid={isValidName(name)}
            className={
              'photon-paragraph-md mt-1 rounded border-b-0 bg-core-gray-100 px-2 py-2.5 text-core-gray-600 focus-visible:border-b-0'
            }
            labelClassName={'photon-caption-sm text-core-gray-900 -mb-9 pt-9'}
            containerClassName={''}
          />
        </div>
        <div>
          <Input
            required
            usePlaceholderAsLabel
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            label={emailFieldLabel}
            isValid={isValidEmail(email)}
            className={
              'photon-paragraph-md mt-1 rounded border-b-0 bg-core-gray-100 px-2 py-2.5 text-core-gray-600 focus-visible:border-b-0'
            }
            labelClassName={'photon-caption-sm text-core-gray-900 -mb-9 pt-9'}
          />
        </div>
        <div className="w-full py-2">
          <If condition={!!savedPaymentInfo}>
            <Then>
              <div className="photon-caption-sm text-sm text-core-gray-900">Card</div>
              <PrefilledCreditCardInput
                savedPayment={savedPaymentInfo as PaymentInfo}
                changeCard={() => setSavedPaymentInfo(undefined)}
              />
            </Then>
            <Else>
              <div className="photon-caption-sm text-sm text-core-gray-900">Card</div>
              <div className="mt-1 h-12 rounded bg-core-gray-100 px-2 py-3">
                <CardElement options={options} />
              </div>
            </Else>
          </If>
        </div>
        <Button
          aria-label="pay-button"
          variant="oxide"
          className="mt-4 flex w-full justify-center rounded-md p-4"
          disabled={isSubmitting || isSubmittingWithWallet}
        >
          <If condition={isSubmitting || isSubmittingWithWallet}>
            <Then>
              <div className="text-white">
                <Translate t={t} i18nKey="processingCheckout">
                  Processing
                </Translate>
                ...
              </div>
            </Then>
            <Else>{amountToPayOneTime}</Else>
          </If>
        </Button>
      </When>
      <div className="-mb-4 pt-4 text-center text-red">
        {errorMessage ? errorMessage : errorMessageWithWallet ? errorMessageWithWallet : ''}
      </div>
    </form>
  )
}

const options = {
  style: {
    base: {
      color: '#7B7979',
      colorText: '#7B7979',
      backgroundColor: '#F5F5F4',
      borderRadius: '8px',
      height: '48px',
      fontSize: '16px',
      fontFamily: 'inherit',
      lineHeight: '28px',
      '::placeholder': {
        color: '#7B7979',
        colorText: '#7B7979',
        fontSize: '16px',
        fontFamily: 'inherit',
      },
    },
  },
}
